import { Box, Icon, Image as PlumaImage, PlainButton } from '@customerio/pluma-components/react';
import { Lightbox, useLightbox } from '@/components/lightbox';
import { useCopilotImageThumbnail } from '../../hooks/use-copilot-image-thumbnail';
import { useCopilotHandleDownload } from '../../hooks/use-copilot-handle-download';
import { getFileIcon } from '../../utils/get-file-icon';
import { FileChipList, type FileData } from './file-chip';
import type { CopilotSession } from '@customerio/hydra-copilot';
import { isEmptyString } from '@/utils/string';
import * as styles from './index.css';

export type { FileData } from './file-chip';

interface AttachmentsProps {
  files: FileData[];
  session?: CopilotSession;
  /** "grid" renders large thumbnail cards (for sent messages). "inline" renders compact chips. */
  variant: 'grid' | 'inline';
  /** Callback when a file is removed — shows close button when provided. */
  onRemove?: (fileId: string) => void;
  /** Only for inline variant — whether chips are read-only. */
  readOnly?: boolean;
}

export function Attachments({ files, session, variant, onRemove, readOnly = true }: AttachmentsProps) {
  const lightbox = useLightbox();

  if (files.length === 0) return null;

  return (
    <>
      {variant === 'grid' ? (
        <Box className={styles.gridContainer}>
          {files.map((file) => (
            <Box key={file.id} className={styles.gridCardWrapper}>
              {onRemove != null ? (
                <PlainButton
                  className={styles.gridCloseButton}
                  onClick={(event: React.MouseEvent) => {
                    event.stopPropagation();
                    onRemove(file.id);
                  }}
                  aria-label={`Remove ${file.name}`}
                >
                  <Icon name="close" size="xs" />
                </PlainButton>
              ) : null}
              <AttachmentCard file={file} session={session} onImageClick={lightbox.open} />
            </Box>
          ))}
        </Box>
      ) : (
        <FileChipList files={files} readOnly={readOnly} onRemove={onRemove} session={session} />
      )}
      <Lightbox src={lightbox.src} onClose={lightbox.close} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Grid card
// ---------------------------------------------------------------------------

function AttachmentCard({
  file,
  session,
  onImageClick,
}: {
  file: FileData;
  session?: CopilotSession;
  onImageClick: (source: string) => void;
}) {
  const isImage = file.mimeType.startsWith('image/');

  const imageUrl = useCopilotImageThumbnail({
    source: file.source,
    isImage,
    readOnly: true,
    session,
  });

  const downloadSource = !isEmptyString(imageUrl) ? imageUrl : file.source;

  const { handleDownload } = useCopilotHandleDownload({
    source: downloadSource,
    filename: file.name,
    session,
  });

  if (isImage && !isEmptyString(imageUrl)) {
    return (
      <PlainButton
        className={styles.gridCard}
        onClick={() => onImageClick(imageUrl)}
        aria-label={file.name}
        cursor="zoom-in"
      >
        <PlumaImage src={imageUrl} alt={file.name} className={styles.gridImage} />
      </PlainButton>
    );
  }

  return (
    <PlainButton className={styles.gridCard} onClick={handleDownload} aria-label={file.name}>
      <Box className={styles.gridDocument}>
        <Icon name={getFileIcon(file.mimeType)} size="lg" color="subtle" />
      </Box>
    </PlainButton>
  );
}
